// ABOUTME: Chat transport for the in-app conversation (#2569 Part 1). Sending
// a message is a fire-and-forget POST /agent/chat ack; the reply is NOT
// returned there — it arrives later over the app's single SSE connection
// (useEventStream -> useHermesReplyStore) as an agent_run_completed /
// agent_run_failed Notification. There is no more token-by-token streaming:
// one pending "thinking" bubble sits on screen until the reply lands.
//
// Replaces useBackendStreaming.ts, which drove the old /agent/chat/stream SSE
// read loop, /agent/chat/resume reconnect polling, and /agent/chat/cancel Stop
// button — all three removed with the old LangGraph chat transport.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { AIChatMessage } from '@/stores/aiChatTypes'
import type { AttachmentRef } from '@/types'
import type { ContextRef } from '@/lib/askAbout'
import { sendChatMessage, AgentApiError } from '@/api/agent'
import { useAIChatStore } from '@/stores/useAIChatStore'
import { useHermesReplyStore } from '@/stores/useHermesReplyStore'
import { useNotificationsStore } from '@/stores/useNotificationsStore'

export const SETTING_UP_STATUS =
  'Setting up your assistant for the first time. This takes about 40 seconds.'

export function startingStatus(seconds?: number): string {
  return seconds
    ? `Marketing is starting up — about ${seconds} seconds.`
    : 'Marketing is starting up.'
}

type AddMessageFn = (id: string, msg: Omit<AIChatMessage, 'id' | 'timestamp'>) => void
type UpdateMessageFn = (convId: string, msgId: string, updates: Partial<AIChatMessage>) => void

interface PendingTurn {
  conversationId: string
  /** The assistant placeholder bubble this turn owns. */
  assistantMessageId: string
  /** The stored message this bubble waits on a reply to. */
  backendMessageId?: string
}

interface LastSentMessage {
  message: string
  conversationId: string
  clientMessageId: string
  attachments?: AttachmentRef[]
  contextRefs?: ContextRef[]
}

/** The pending turns a run's outcome answers, by stored message id. Only an outcome that
 *  names none (an older payload) falls back to the oldest open turn. */
function takeAnsweredTurns(
  turns: PendingTurn[],
  answers: string[] | undefined
): { taken: PendingTurn[]; open: PendingTurn[] } {
  if (answers === undefined) return { taken: turns.slice(0, 1), open: turns.slice(1) }
  const ids = new Set(answers)
  const taken = turns.filter((t) => t.backendMessageId && ids.has(t.backendMessageId))
  return { taken, open: turns.filter((t) => !taken.includes(t)) }
}

export function useHermesChat() {
  const [pendingCount, setPendingCount] = useState(0)
  const [hasFailedMessage, setHasFailedMessage] = useState(false)
  // Turns awaiting a reply, oldest first. A run's outcome names the messages it
  // answers (`answers`): one turn can answer several when a message sent mid-turn
  // was steered into it. Without names, the oldest open placeholder takes it.
  const pendingTurnsRef = useRef<PendingTurn[]>([])
  const callbacksRef = useRef<{ addMessage: AddMessageFn; updateMessage: UpdateMessageFn } | null>(
    null
  )
  const lastSentRef = useRef<LastSentMessage | null>(null)

  const send = useCallback(
    async (
      message: string,
      conversationId: string,
      clientMessageId: string,
      addMessage: AddMessageFn,
      updateMessage: UpdateMessageFn,
      attachments?: AttachmentRef[],
      contextRefs?: ContextRef[]
    ) => {
      callbacksRef.current = { addMessage, updateMessage }
      lastSentRef.current = { message, conversationId, clientMessageId, attachments, contextRefs }
      setHasFailedMessage(false)

      if (!navigator.onLine) {
        updateMessage(conversationId, clientMessageId, { status: 'failed' })
        setHasFailedMessage(true)
        return
      }

      // The pending "thinking" bubble — same rendering as the old streaming
      // placeholder (ChatMessage shows the typing indicator whenever
      // isStreaming is true and content is still empty).
      addMessage(conversationId, { role: 'assistant', content: '', isStreaming: true })
      // Read back the id we just appended: the tail is only guaranteed to be
      // THIS message the instant after addMessage returns.
      const assistantMessageId = (() => {
        const conversation = useAIChatStore
          .getState()
          .conversations.find((c) => c.id === conversationId)
        const last = conversation?.messages[conversation.messages.length - 1]
        return last?.id ?? null
      })()

      setPendingCount((n) => n + 1)

      try {
        const ack = await sendChatMessage(message, attachments, contextRefs)
        // The stored row's id lets a thread restore swap this bubble for it.
        updateMessage(conversationId, clientMessageId, {
          status: 'sent',
          metadata: { backend_message_id: ack.message_id },
        })
        if (assistantMessageId) {
          pendingTurnsRef.current.push({
            conversationId,
            assistantMessageId,
            backendMessageId: ack.message_id,
          })
          if (ack.assistant_setting_up) {
            updateMessage(conversationId, assistantMessageId, {
              pendingStatus: SETTING_UP_STATUS,
            })
          } else if (ack.assistant_starting_seconds) {
            updateMessage(conversationId, assistantMessageId, {
              pendingStatus: startingStatus(ack.assistant_starting_seconds),
            })
          }
        }
      } catch (err) {
        updateMessage(conversationId, clientMessageId, { status: 'failed' })
        setHasFailedMessage(true)
        if (assistantMessageId) {
          const message =
            err instanceof AgentApiError
              ? err.message
              : 'Could not reach the server. Check your connection and try again.'
          updateMessage(conversationId, assistantMessageId, {
            content: message,
            isStreaming: false,
            isError: true,
          })
        }
        setPendingCount((n) => Math.max(0, n - 1))
      }
    },
    []
  )

  // Reply/failure delivery — reacts to whatever useEventStream published from
  // the app's one SSE connection. Deliberately does not open a stream of its
  // own: this subscribes directly to the store rather than mirroring it into
  // local state through a selector + effect, so the update to `pendingCount`
  // happens inside the subscription callback (an external event), never
  // synchronously in the effect body itself.
  const processedNotificationIdRef = useRef<string | null>(null)
  useEffect(() => {
    return useHermesReplyStore.subscribe((state) => {
      const event = state.lastEvent
      if (!event) return
      if (processedNotificationIdRef.current === event.notificationId) return
      processedNotificationIdRef.current = event.notificationId

      const callbacks = callbacksRef.current
      if (event.kind === 'waiting') {
        // The turns stay open: only their status line changes until the reply lands.
        const waiting = takeAnsweredTurns(pendingTurnsRef.current, event.answers).taken
        for (const turn of waiting) {
          callbacks?.updateMessage(turn.conversationId, turn.assistantMessageId, {
            pendingStatus: startingStatus(event.etaSeconds),
          })
        }
        return
      }
      const answered = takeAnsweredTurns(pendingTurnsRef.current, event.answers)
      pendingTurnsRef.current = answered.open
      const turn = answered.taken[answered.taken.length - 1]
      if (!turn || !callbacks) return
      setPendingCount((n) => Math.max(0, n - answered.taken.length))
      // One reply answers every message the turn took: it goes under the latest, and
      // the earlier placeholders are removed rather than left as empty bubbles.
      for (const earlier of answered.taken.slice(0, -1)) {
        useAIChatStore.getState().removeMessage(earlier.conversationId, earlier.assistantMessageId)
      }

      if (event.kind === 'reply') {
        callbacks.updateMessage(turn.conversationId, turn.assistantMessageId, {
          content: event.content || 'No response received.',
          isStreaming: false,
          pendingStatus: undefined,
          ...(event.messageId ? { metadata: { backend_message_id: event.messageId } } : {}),
        })
      } else {
        callbacks.updateMessage(turn.conversationId, turn.assistantMessageId, {
          content: event.error || 'The assistant run failed. Please try again.',
          isStreaming: false,
          isError: true,
          pendingStatus: undefined,
        })
      }
      // A reply is also always a good moment to refresh the bell badge.
      void useNotificationsStore.getState().fetchNotifications()
    })
  }, [])

  const retryLastMessage = useCallback(() => {
    const last = lastSentRef.current
    const callbacks = callbacksRef.current
    if (!last || !callbacks) return
    callbacks.updateMessage(last.conversationId, last.clientMessageId, { status: 'pending' })
    void send(
      last.message,
      last.conversationId,
      last.clientMessageId,
      callbacks.addMessage,
      callbacks.updateMessage,
      last.attachments,
      last.contextRefs
    )
  }, [send])

  return {
    pendingCount,
    hasFailedMessage,
    send,
    retryLastMessage,
  }
}
